import React, { useCallback, useEffect, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { Box, Divider, Flex, Icon, Spinner, styled, Text } from '@chilipiper/design-system'
import {
  Button,
  Events,
  Input,
  TextArea,
  Checkbox,
  Radio,
  InputNumber,
  Select,
  PillSelect,
} from '@chilipiper/design-system/src/new'
import { differenceInMinutes, formatInTimeZone } from '@chilipiper/date-time'
import { IconError, IconWarning } from '@chilipiper/icons/figma-generated/src/icon'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { MeetingLocations } from '@chilipiper/api-client/src/meeting-types'
import { useTranslations } from '../../../context/TranslationContext'
import { PreviousSchedule } from './PreviousSchedule'
import { Times, TimesEvents } from './Times'
import { Days } from './Days'
import { customerColorCss } from './styles'
import { GuestFormValues } from './helpers'

type FieldType = {
  dataType?: DataFieldDataType
  description?: string
  error?: string
  id: string
  initialValue?: string
  hiddenValue?: string
  isRequired: boolean
  label?: string
}

interface Props {
  availableDays: Record<string, boolean>
  bookedTime?: { end: string; start: string }
  cancelRescheduling: () => void
  durationInMinutes: number
  fields?: Array<FieldType> | undefined
  guestEmail?: string
  isLoading: boolean
  isNoAvailableAssignee?: boolean
  isSlotTaken?: boolean
  meetingTypeId?: string
  locations?: MeetingLocations
  onSchedule: (guestFormData: GuestFormValues) => void
  onSelectedTimeChange: (time: string) => void
  onTimezoneChange: (timezone: string) => void
  selectedTime?: string
  startTimes?: Array<string>
  suggestedTimesToggle?: React.ReactNode
  timezone: string
}

export const BookingMeeting = ({
  startTimes,
  availableDays,
  bookedTime,
  durationInMinutes,
  isLoading,
  meetingTypeId,
  onSelectedTimeChange,
  timezone,
  onTimezoneChange,
  onSchedule,
  cancelRescheduling,
  selectedTime,
  isSlotTaken,
  isNoAvailableAssignee,
  suggestedTimesToggle,
  fields,
  guestEmail,
}: Props) => {
  let initialGuestForm = Object.fromEntries(
    fields
      ?.filter(field => field.initialValue || field.hiddenValue)
      .map(field => [field.id, field.hiddenValue ?? field.initialValue ?? '']) ?? []
  )
  if (guestEmail) {
    initialGuestForm = { ...initialGuestForm, PersonEmail: guestEmail }
  }
  const [pickMeetingType, loadingDatesAndTimes, confirmMeeting] = useTranslations([
    'pickMeetingType',
    'loadingDatesAndTimes',
    'confirmMeeting',
  ])
  const [guestForm, setGuestForm] = useState<GuestFormValues>()
  const guestFormWithInitialData = { ...initialGuestForm, ...guestForm }
  const [immediateFieldErrors, setImmediateFieldErrors] = useState<Record<string, string>>()
  const fieldsWithErrors = fields
    ?.filter(field => !field.hiddenValue)
    .map(field => {
      return { ...field, error: immediateFieldErrors?.[field.id] ?? field.error }
    })

  const [selectedDay, setSelectedDay] = useState<string>()
  const emailInputRef = useRef<Events<'focus'>>(null)
  const buttonWrapperRef = useRef<HTMLDivElement>(null)
  const timesEventsRef = useRef<TimesEvents>(null)
  const daysWrapperRef = useRef<HTMLDivElement>(null)

  const getDayWithSlots = useCallback(
    (preferredDay: string, timezone: string) => {
      const hasAtLeastOneSlot = startTimes?.[0]
      const preferredDayHasNoSlots = !startTimes?.some(
        time => formatInTimeZone(new Date(time), timezone, 'yyyy-MM-dd') === preferredDay
      )
      if (hasAtLeastOneSlot && preferredDayHasNoSlots) {
        // get slot with the closest date
        const getDifference = (time: string) =>
          Math.abs(
            differenceInMinutes(
              new Date(formatInTimeZone(new Date(time), timezone, 'yyyy-MM-dd')),
              new Date(preferredDay)
            )
          )
        const sortedTimes = [...startTimes].sort(
          (aTime, bTime) => getDifference(aTime) - getDifference(bTime)
        )
        invariant(sortedTimes[0], 'sortedTimes must have at least one element')
        return formatInTimeZone(sortedTimes[0], timezone, 'yyyy-MM-dd')
      }
      return preferredDay
    },
    [startTimes]
  )
  useEffect(() => {
    const dayWithSlots = getDayWithSlots(
      selectedDay ?? formatInTimeZone(Date.now(), timezone, 'yyyy-MM-dd'),
      timezone
    )
    setSelectedDay(dayWithSlots)
  }, [startTimes])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    if (!selectedDay || !daysWrapperRef.current) {
      return
    }
    const dayElement = daysWrapperRef.current.querySelector(`[data-day="${selectedDay}"]`)
    if (dayElement) {
      timeoutId = setTimeout(() => {
        dayElement.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
      }, 10 /* For some reason there needs to be a delay here to work when changing timezones */)
    }
    return () => clearTimeout(timeoutId)
  }, [selectedDay])

  const handleTimezoneChange = (newTimezone: string) => {
    onTimezoneChange(newTimezone)
    const dayWithSlots = getDayWithSlots(
      formatInTimeZone(selectedDay ?? Date.now(), newTimezone, 'yyyy-MM-dd'),
      newTimezone
    )
    handleSelectDay(dayWithSlots)
  }

  const getTimeSlotsForDay = (day?: string) => {
    if (!day) {
      return []
    }
    return startTimes?.filter(
      time => formatInTimeZone(new Date(time), timezone, 'yyyy-MM-dd') === day
    )
  }

  /**
   * Handle selecting a day
   *
   * @param day - Format "yyyy-MM-dd"
   */
  const handleSelectDay = (day: string) => {
    setSelectedDay(day)
    if (getTimeSlotsForDay(day)?.length) {
      timesEventsRef.current?.scrollIntoView()
    }
  }

  if (!meetingTypeId) {
    return (
      <Flex
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        h='132px'
        data-test-id='BookingMeeting-noMeetingType'
      >
        <Icon color='fg/moderate' icon={IconError} mb={2} />
        <Text color='fg/moderate' textStyle='body-multiline' px={7} textAlign='center'>
          {pickMeetingType ?? 'Pick meeting type'}
        </Text>
      </Flex>
    )
  }
  if (isLoading) {
    return (
      <Flex justifyContent='center' alignItems='center' h='132px'>
        <Spinner mr={2} />
        <Text color='fg/moderate' textStyle='body-multiline'>
          {loadingDatesAndTimes ?? 'Loading dates and times'}
        </Text>
      </Flex>
    )
  }
  const email = (
    Array.isArray(guestFormWithInitialData['PersonEmail'])
      ? (guestFormWithInitialData['PersonEmail']?.[0] ?? '')
      : (guestFormWithInitialData['PersonEmail'] ?? '')
  ).trim()
  return (
    <form
      onInvalid={e => {
        if (
          'name' in e.target &&
          typeof e.target.name === 'string' &&
          'validationMessage' in e.target &&
          typeof e.target.validationMessage === 'string'
        ) {
          setImmediateFieldErrors({
            ...immediateFieldErrors,
            [e.target.name]: e.target.validationMessage,
          })
        }
      }}
      onSubmit={e => {
        e.preventDefault()
        onSchedule(guestFormWithInitialData)
      }}
    >
      <BannersOrDivider
        bookedTime={bookedTime}
        timezone={timezone}
        isNoAvailableAssignee={isNoAvailableAssignee}
        isSlotTaken={isSlotTaken}
        cancelRescheduling={cancelRescheduling}
      />
      <Days
        daysWrapperRef={daysWrapperRef}
        availableDays={availableDays}
        selectedDay={selectedDay}
        onSelectDay={handleSelectDay}
        suggestedTimesToggle={suggestedTimesToggle}
        timezone={timezone}
      />

      <Times
        eventsRef={timesEventsRef}
        time={selectedTime}
        onTimeChange={time => {
          onSelectedTimeChange(time)
          if (!email) {
            emailInputRef.current?.focus()
          } else {
            buttonWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }}
        timezone={timezone}
        startTimes={getTimeSlotsForDay(selectedDay)}
        onTimezoneChange={handleTimezoneChange}
        durationInMinutes={durationInMinutes}
      />

      {(fieldsWithErrors?.length ?? 0) > 0 && <Divider m={5} mt={1} />}
      {fieldsWithErrors?.map(field => {
        return (
          <Field
            key={field.id}
            field={field}
            value={
              Array.isArray(guestFormWithInitialData[field.id])
                ? (guestFormWithInitialData[field.id]?.[0] ?? '')
                : (guestFormWithInitialData[field.id] as string)
            }
            onChange={newValue => {
              if (field.error) {
                const newErrors = { ...immediateFieldErrors }
                delete newErrors[field.id]
                setImmediateFieldErrors(newErrors)
              }
              setGuestForm({ ...guestForm, [field.id]: newValue })
            }}
          />
        )
      })}

      <Box mx={5} mb={6} ref={buttonWrapperRef}>
        <ButtonThemeWrapper>
          <Button isFullWidth variant='major' isDisabled={!email || !selectedTime} type='submit'>
            {confirmMeeting ?? 'Confirm Meeting'}
          </Button>
        </ButtonThemeWrapper>
      </Box>
    </form>
  )
}

const ButtonThemeWrapper = styled(Box)`
  button:not([disabled]) {
    &,
    :hover,
    :active,
    :focus {
      ${customerColorCss}
    }
  }
`

export const getCurrentTime = (time: Date, locale: string, timezone: string) => {
  try {
    return time.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    })
  } catch (e) {
    console.error(e)
    return 'Unknown'
  }
}

const BannersOrDivider = ({
  bookedTime,
  isSlotTaken,
  isNoAvailableAssignee,
  timezone,
  cancelRescheduling,
}: Pick<
  Props,
  'isNoAvailableAssignee' | 'bookedTime' | 'isSlotTaken' | 'timezone' | 'cancelRescheduling'
>) => {
  const [timeslotSelectionIsNoLongerAvailable] = useTranslations([
    'timeslotSelectionIsNoLongerAvailable',
  ])
  if (bookedTime || isSlotTaken) {
    return (
      <Flex flexDirection='column' gap={2}>
        {bookedTime !== undefined && (
          <PreviousSchedule date={bookedTime} timezone={timezone} onClick={cancelRescheduling} />
        )}
        {(isSlotTaken || isNoAvailableAssignee) && (
          <Box
            mx={3}
            bg='bg/section-emphasis'
            boxShadow='border/on-major'
            borderRadius={12}
            overflow='hidden'
          >
            <Flex gap={3} px={6} py={3}>
              <Icon mt={1} flexShrink={0} icon={IconWarning} color='fg/warning' />
              <Text color='fg/major' textStyle='body-multiline'>
                {timeslotSelectionIsNoLongerAvailable ??
                  'Timeslot selection is no longer available. Please select a new one.'}
              </Text>
            </Flex>
          </Box>
        )}
      </Flex>
    )
  }
  return <Divider mx={5} />
}

const Field = ({
  field,
  value,
  onChange,
}: {
  field: FieldType
  onChange: (value: string) => void
  value: string
}) => {
  const commonProps = {
    mx: 5,
    mb: 3,
  }
  const label = field.label ?? ''
  const description = field.description ?? 'Enter...'
  if (
    field.dataType?.type === 'ShortText' ||
    field.dataType?.type === 'Email' ||
    field.dataType?.type === 'Phone'
  ) {
    return (
      <Input
        key={field.id}
        {...commonProps}
        variant='regular'
        type={(() => {
          if (field.dataType?.type === 'Email') {
            return 'email'
          }
          if (field.dataType?.type === 'Phone') {
            return 'tel'
          }
          return 'text'
        })()}
        name={field.id}
        label={<Input.Label position='top'>{label}</Input.Label>}
        placeholder={description}
        isRequired={field.isRequired}
        errorMessage={!value ? field.error : undefined}
        value={value}
        onChange={onChange}
      />
    )
  }
  if (field.dataType?.type === 'LongText') {
    return (
      <TextArea
        key={field.id}
        {...commonProps}
        variant='regular'
        label={<TextArea.Label position='top'>{label}</TextArea.Label>}
        placeholder={description}
        name={field.id}
        isRequired={field.isRequired}
        errorMessage={!value ? field.error : undefined}
        value={value}
        onChange={onChange}
      />
    )
  }
  if (field.dataType?.type === 'Number') {
    return (
      <InputNumber
        key={field.id}
        {...commonProps}
        variant='regular'
        label={<InputNumber.Label position='top'>{label}</InputNumber.Label>}
        placeholder={description}
        name={field.id}
        isRequired={field.isRequired}
        errorMessage={!value ? field.error : undefined}
        value={Number(value)}
        onChange={newValue => onChange(String(newValue))}
      />
    )
  }
  if (field.dataType?.type === 'Checkbox') {
    const selectedValues = value?.split(';').filter(value => value !== '') ?? []
    return (
      <Box key={field.id} {...commonProps}>
        <Text display='block' textStyle='form-label' color='fg/major' mb={1}>
          {field.label}
        </Text>
        <Text display='block' textStyle='body-multiline' color='fg/moderate' mb={3}>
          {field.description}
        </Text>
        <Box overflow='hidden' boxShadow='border/on-major' borderRadius={8}>
          <PillSelect
            variant='regular'
            isExpandable={true}
            label={
              <PillSelect.Label position='hidden'>
                {field.label ?? `Select ${field.dataType.type} value`}
              </PillSelect.Label>
            }
            onChange={value => {
              onChange(value.join(';'))
            }}
            values={selectedValues}
          >
            {field.dataType.values.map(value => (
              <PillSelect.Item key={value} value={value}>
                {value}
              </PillSelect.Item>
            ))}
          </PillSelect>
        </Box>
        {field.error && !value && (
          <Text
            display='block'
            textStyle='body-multiline-tight'
            data-test-id='input-error-message'
            color='fg/caution'
            mt={2}
          >
            {field.error}
          </Text>
        )}
      </Box>
    )
  }
  if (field.dataType?.type === 'RadioButton') {
    return (
      <Box key={field.id} {...commonProps}>
        <Radio.Group
          label={
            <Radio.GroupLabel position='top' description={field.description}>
              {label}
            </Radio.GroupLabel>
          }
          value={value}
          onChange={onChange}
          name={field.id}
        >
          {field.dataType.values.map(value => (
            <Radio.Item key={value} label={value} value={value} />
          ))}
        </Radio.Group>
        {field.error && !value && (
          <Text
            display='block'
            textStyle='body-multiline-tight'
            data-test-id='input-error-message'
            color='fg/caution'
            mt={2}
          >
            {field.error}
          </Text>
        )}
      </Box>
    )
  }
  if (field.dataType?.type === 'TrueFalse') {
    return (
      <Box key={field.id} {...commonProps}>
        <Box overflow='hidden' boxShadow='border/on-major' borderRadius={8}>
          <Checkbox
            label={<Checkbox.Label>{field.label ?? 'Checkbox'}</Checkbox.Label>}
            onChange={value => {
              onChange(value ? 'true' : 'false')
            }}
            isChecked={value === 'true'}
          />
        </Box>
      </Box>
    )
  }
  if (field.dataType?.type === 'Picklist') {
    return (
      <Select
        key={field.id}
        {...commonProps}
        label={
          <Select.Label position='top' description={field.description}>
            {label}
          </Select.Label>
        }
        errorMessage={!value ? field.error : undefined}
        value={value}
        onChange={onChange}
      >
        {field.dataType.values.map(value => (
          <Select.Item key={value} value={value}>
            {value}
          </Select.Item>
        ))}
      </Select>
    )
  }
  return null
}
